// Minimal data required to create a party (personne physique / personne morale)
// in a given jurisdiction. Spec 2026-07-13-01-refonte-contacts-parties.
//
// Three tiers (see the design discussion):
//   0 — identify:        prénom + nom de naissance (BLOCKING at creation)
//   1 — contract/notify: date/place of birth, address, phone + (morale) legal
//                        representative (RECOMMENDED)
//   2 — authentic act:   nationality, marital regime, Kbis… (out of creation)
//
// This module centralises tier-0 (required) and tier-1 (recommended) for the
// person form; the company statutory identifier lives in `company-identifiers.ts`.

export type Jurisdiction = 'fr'

export interface PartyFieldDef {
  /** Property key under `users.properties`. */
  key: string
  /** i18n label key (namespace `users`). */
  labelKey: string
  type: 'text' | 'date' | 'tel'
  /** Tier 0 (blocking) when true; tier 1 (recommended) when false. */
  required: boolean
  placeholderKey?: string
}

/** A personne physique is a `contact` (non-auth) or a `user` (auth) person. */
export function isPhysicalPersonType(type: string): boolean {
  return type === 'contact' || type === 'user'
}

/** A personne morale is a `company` (with a legalForm). */
export function isLegalEntityType(type: string): boolean {
  return type === 'company'
}

// État-civil fields for a personne physique (France). Tier-0 = nom de naissance,
// prénom (the identity that disambiguates homonyms); date + lieu de naissance are
// tier-1 (recommended, not blocking — most contacts are created from a phone call
// or an e-mail without notarial-grade identity in hand).
const PERSON_FIELDS_FR: PartyFieldDef[] = [
  { key: 'firstName', labelKey: 'create.person.first_name', type: 'text', required: true },
  { key: 'lastName', labelKey: 'create.person.birth_name', type: 'text', required: true },
  { key: 'usageName', labelKey: 'create.person.usage_name', type: 'text', required: false },
  { key: 'dateOfBirth', labelKey: 'create.person.date_of_birth', type: 'date', required: false },
  {
    key: 'placeOfBirth',
    labelKey: 'create.person.place_of_birth',
    type: 'text',
    required: false,
    placeholderKey: 'create.person.place_of_birth_placeholder',
  },
  { key: 'nationality', labelKey: 'create.person.nationality', type: 'text', required: false },
  {
    key: 'address',
    labelKey: 'create.person.address',
    type: 'text',
    required: false,
    placeholderKey: 'create.person.address_placeholder',
  },
  {
    key: 'phone',
    labelKey: 'create.person.mobile_phone',
    type: 'tel',
    required: false,
    placeholderKey: 'create.person.mobile_phone_placeholder',
  },
]

/** État-civil field definitions for a personne physique in the jurisdiction. */
export function personFields(_jurisdiction: Jurisdiction = 'fr'): PartyFieldDef[] {
  return PERSON_FIELDS_FR
}

/**
 * Display name for a personne physique: `prénom` + `nom d'usage` (falling back to
 * the nom de naissance). Notarial acts use the nom de naissance, but the display
 * name follows the nom d'usage when set.
 */
export function derivePersonName(props: Record<string, unknown>): string {
  const first = String(props.firstName || '').trim()
  const last = String(props.usageName || props.lastName || '').trim()
  return `${first} ${last}`.trim()
}

/**
 * Validate the tier-0 (blocking) requirements for a personne physique. Returns a
 * map of `person_<key>` → message for each missing required field.
 */
export function validatePersonRequired(
  type: string,
  props: Record<string, unknown>,
  t: (key: string) => string,
): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!isPhysicalPersonType(type)) return errors
  for (const f of personFields()) {
    if (f.required && !String(props[f.key] || '').trim()) {
      errors[`person_${f.key}`] = t('create.person.required')
    }
  }
  return errors
}
